/// <reference types="vite-plugin-svgr/client" />
import { useState, ReactElement, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import styles from './signup.page.module.scss';
import {
    ConfirmSignupVerification,
    RequestSignupVerification,
    Signup,
    SsoSignIn
} from '@/me/components';
import { UICard, UISpacer, UIText } from '@/ui';
import { RegionSelector, Accolades } from '@/common/components';
import SafepointLogo from '@/common/assets/images/safepoint-logo-dark.svg?react';
import { Trans } from 'react-i18next';

type Step = 'email' | 'code' | 'details';

export function SignupPage(): ReactElement {
    const navigate = useNavigate();

    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [token, setToken] = useState('');

    return (
        <div className={styles.page}>
            <div className={styles.quoteHalf}>
                <div className={styles.quoteContainer}>
                    <Accolades />
                </div>
            </div>
            <div className={styles.loginHalf}>
                <SafepointLogo width={300} />
                <UISpacer multiplier={4} />
                <UICard className={styles.loginCard}>
                    {step === 'email' && (
                        <>
                            <RequestSignupVerification
                                defaultEmail={email}
                                onSuccess={(data) => {
                                    setEmail(data.email);
                                    setStep('code');
                                }}
                            />
                            {/*
                                Only on the first step: the later ones are
                                partway through the email flow, and signing in
                                with a provider there would abandon it.
                                ssoLogin creates the account when none exists,
                                so this is the whole signup.
                            */}
                            <SsoSignIn
                                onSuccess={() => {
                                    void navigate('/', { replace: true });
                                }}
                            />
                        </>
                    )}
                    {step === 'code' && (
                        <ConfirmSignupVerification
                            email={email}
                            onChangeEmail={() => {
                                setStep('email');
                            }}
                            onSuccess={(data) => {
                                setToken(data.token);
                                setStep('details');
                            }}
                        />
                    )}
                    {step === 'details' && (
                        <Signup
                            token={token}
                            onSuccess={() => {
                                void navigate('/', { replace: true });
                            }}
                        />
                    )}
                </UICard>
                <UISpacer multiplier={1} />
                <RegionSelector variant="compact" />
                <UISpacer multiplier={2} />

                <UIText variant="basic">
                    <Trans
                        ns="pages"
                        i18nKey="pages.signup.already_have_an_account"
                        components={{ a: <LoginLink /> }}
                    />
                </UIText>
            </div>
        </div>
    );
}

/**
 * The link inside the account prompt. Wrapping it in a component lets the whole
 * sentence be one translated string, so a language can order it as it needs.
 */
function LoginLink({ children }: { children?: ReactNode }): ReactElement {
    return (
        <Link to={'/login'} style={{ textDecoration: 'none' }}>
            <UIText variant="basic" type="link" underlineOnHover>
                {children}
            </UIText>
        </Link>
    );
}
